<script setup lang="ts">
  import { computed, inject, ref, type Ref, onMounted } from 'vue'
  import { useRoute } from 'vue-router'
  import { QuestionnairesService } from '@/types/generated'
  import { genericErrorToast } from '@/lib/toasts'
  import Questionaire from '@/components/questionaire/questionnaireFormV3'
  import { LoaderCircle } from 'lucide-vue-next'
  import client404 from '@/components/404/404.vue'
  import Header from '@/components/clientPortal/Header.vue'
  import type { ClientOrganisation } from '@/types/generated'

  const route = useRoute()
  const questionnaireUuid = route.params.uuid as string

  const org = inject<Ref<ClientOrganisation | null>>('org')
  const sidebarVisible = inject<Ref<boolean>>('sidebarVisible', ref(true))
  const shouldShowSidebar = inject<Ref<boolean>>('shouldShowSidebar', ref(true))

  const loading = ref(true)
  const questionnaire = ref<any>(null)

  const parentQuestionnaire = inject<Ref<any | null>>('questionnaire')

  const hasQuestionnaire = computed(() => questionnaire.value !== null)

  const metadata = computed(() => {
    if (!questionnaire.value) return {}

    return {
      title: questionnaire.value.title,
      totalQuestionCount: questionnaire.value.schema?.length || 0,
      isPreview: true,
      isExternal: false
    }
  })

  const rawSchema = computed(() => questionnaire.value?.schema || [])

  onMounted(async () => {
    try {
      const fetchedQuestionnaire = await QuestionnairesService.questionnairesRetrieve({
        uuid: questionnaireUuid
      })
      questionnaire.value = fetchedQuestionnaire

      if (
        fetchedQuestionnaire &&
        'org' in fetchedQuestionnaire &&
        fetchedQuestionnaire.org &&
        org
      ) {
        org.value = fetchedQuestionnaire.org as unknown as ClientOrganisation
      }

      if (parentQuestionnaire) {
        parentQuestionnaire.value = fetchedQuestionnaire
      }
    } catch (error) {
      console.error('Error fetching questionnaire preview:', error)
      genericErrorToast()
    } finally {
      loading.value = false
    }
  })
</script>

<template>
  <div
    v-if="loading"
    class="flex h-screen items-center justify-center"
    role="status"
  >
    <LoaderCircle
      :size="100"
      class="animate-spin"
      aria-hidden="true"
    />
    <span class="sr-only">Loading questionnaire preview...</span>
  </div>

  <template v-else>
    <div
      v-if="hasQuestionnaire && questionnaire"
      class="min-h-screen w-full bg-gray-50"
    >
      <Header
        :title="`${questionnaire.title} (Preview)`"
        :show-back-button="false"
        :sidebar-visible="sidebarVisible"
        :should-show-sidebar="shouldShowSidebar"
        title-data-cy="ClientPortalQuestionnairePreviewViewMobileTitle"
      />

      <div class="min-h-[calc(100vh-85px)] pt-14 md:pt-[85px]">
        <Questionaire
          :rawSchema="rawSchema"
          :metaData="metadata"
        />
      </div>
    </div>

    <client404 v-else />
  </template>
</template>
